import { Box, Typography, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const MotionBox = motion.create(Box);
const MotionSvg = motion.svg;
const MotionText = motion.text;
const MotionPath = motion.path;
const MIN_VISIBLE_MS = 2000;

const loaderVariants = {
  hidden: {
    opacity: 0,
    scale: 0.96,
    y: 16,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: "easeOut",
      when: "beforeChildren",
      staggerChildren: 0.12,
    },
  },
};

const vanishPulse = {
  hidden: {
    opacity: 0,
    scale: 0.92,
    y: 18,
  },
  visible: (delay = 0) => ({
    opacity: [0, 1, 1, 0.35, 1],
    scale: [0.92, 1, 1.03, 0.96, 1],
    y: [18, 0, 0, -8, 0],
    transition: {
      duration: 3.6,
      delay,
      repeat: Infinity,
      repeatType: "loop",
      ease: "easeInOut",
    },
  }),
};

function FiLogo() {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "1 / 1",
        display: "grid",
        placeItems: "center",
      }}>
      <Box
        sx={{
          position: "absolute",
          inset: "14%",
          borderRadius: "34px",
          background:
            "radial-gradient(circle at 35% 30%, rgba(0, 36, 81, 0.2), rgba(0, 36, 81, 0.06) 52%, transparent 72%)",
          filter: "blur(10px)",
        }}
      />

      <MotionSvg
        viewBox="0 0 240 240"
        width="100%"
        height="100%"
        aria-hidden="true"
        initial="hidden"
        animate="visible"
        variants={loaderVariants}
        style={{ overflow: "visible", willChange: "transform, opacity" }}>
        <motion.g variants={vanishPulse} custom={0}>
          <path
            d="M66 202V126H52V106H66V95C66 66 86 46 116 46H124C148 46 168 64 168 88V104H146V91C146 78 138 70 125 70H117C102 70 90 82 90 96V106H132V126H90V202H66Z"
            fill="#0c1e35"
          />
          <rect x="52" y="106" width="22" height="20" fill="#0c1e35" />
        </motion.g>

        <motion.g variants={vanishPulse} custom={0.18}>
          <rect x="146" y="106" width="24" height="96" fill="#1bb8cf" />
        </motion.g>
      </MotionSvg>
    </Box>
  );
}

function AbstractMarkLogo() {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "0.82 / 1",
        display: "grid",
        placeItems: "center",
      }}>
      <Box
        sx={{
          position: "absolute",
          inset: "12% 18%",
          borderRadius: "40px",
          background:
            "radial-gradient(circle at 45% 34%, rgba(68, 77, 214, 0.18), rgba(68, 77, 214, 0.03) 56%, transparent 72%)",
          filter: "blur(12px)",
        }}
      />

      <MotionSvg
        viewBox="0 0 180 220"
        width="100%"
        height="100%"
        aria-hidden="true"
        initial="hidden"
        animate="visible"
        variants={loaderVariants}
        style={{ overflow: "visible", willChange: "transform, opacity" }}>
        <MotionPath
          d="M52 202C69 168 76 139 78 108C80 75 94 48 124 18C121 49 111 74 100 95C89 116 86 144 94 171C99 187 97 199 90 214"
          fill="none"
          stroke="#1bb6ea"
          strokeWidth="16"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={vanishPulse}
          custom={0}
        />
        <MotionPath
          d="M19 196C47 188 75 179 99 170C129 159 152 142 158 118C164 92 151 69 123 53C109 45 103 33 104 18C126 26 144 40 154 57C167 78 166 101 154 120C139 143 111 159 74 173C54 181 35 189 18 199"
          fill="none"
          stroke="#3f46bf"
          strokeWidth="15"
          strokeLinecap="round"
          strokeLinejoin="round"
          variants={vanishPulse}
          custom={0.18}
        />
      </MotionSvg>
    </Box>
  );
}

export default function LoadingMainPage({
  isLoading,
  widthProp,
  logoVariant = "letters",
  statusText = "",
  minVisibleMs = MIN_VISIBLE_MS,
}) {
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const [shouldRender, setShouldRender] = useState(isLoading);
  const visibleSinceRef = useRef(isLoading ? Date.now() : null);

  useEffect(() => {
    let hideTimer;

    if (isLoading) {
      setShouldRender(true);
      if (!visibleSinceRef.current) {
        visibleSinceRef.current = Date.now();
      }
    } else if (shouldRender) {
      const elapsed = visibleSinceRef.current
        ? Date.now() - visibleSinceRef.current
        : minVisibleMs;

      const remaining = Math.max(0, minVisibleMs - elapsed);

      hideTimer = setTimeout(() => {
        setShouldRender(false);
        visibleSinceRef.current = null;
      }, remaining);
    }

    return () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
    };
  }, [isLoading, minVisibleMs, shouldRender]);

  if (!shouldRender) return null;

  const isAbstractVariant = logoVariant === "abstract";

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: theme.zIndex.modal + 1,
        display: "grid",
        placeItems: "center",
        px: 2,
        backgroundColor: "rgba(235, 243, 255, 0.34)",
        background:
          "radial-gradient(circle at 16% 12%, rgba(255, 255, 255, 0.62), rgba(219, 233, 252, 0.55) 46%, rgba(198, 220, 247, 0.5) 100%)",
        backdropFilter: reducedMotion ? "none" : "blur(6px)",
      }}>
      <MotionBox
        variants={loaderVariants}
        initial="hidden"
        animate="visible"
        sx={{
          display: "grid",
          placeItems: "center",
          gap: 1.25,
          willChange: "transform, opacity",
        }}>
        <Box
          sx={{
            width: widthProp || { xs: 210, sm: 290, md: 330 },
            height: widthProp || { xs: 210, sm: 290, md: 330 },
            display: "grid",
            placeItems: "center",
            filter: reducedMotion
              ? "none"
              : "drop-shadow(0 0 24px rgba(0, 36, 81, 0.22))",
            transform: "translateZ(0)",
          }}>
          {isAbstractVariant ? <AbstractMarkLogo /> : <FiLogo />}
        </Box>

        {statusText ? (
          <Typography
            component={motion.p}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: "easeOut", delay: 0.08 }}
            sx={{
              m: 0,
              color: "#17345a",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              fontSize: { xs: "0.8rem", sm: "0.88rem" },
              textAlign: "center",
            }}>
            {statusText}
          </Typography>
        ) : null}
      </MotionBox>
    </Box>
  );
}

export function LoadingMainPageAbstract(props) {
  return <LoadingMainPage {...props} logoVariant="abstract" />;
}

export function LoadingMainPageLetters(props) {
  return <LoadingMainPage {...props} logoVariant="letters" />;
}
